import HighchartsReact from "highcharts-react-official";
import Highcharts, { color } from "highcharts";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import SelectComponent from "components/select/Select";
import moment from "moment";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ButtonComponent from "components/button/Button";
import Loading from "components/loading/Loading";
import { apiExportRecordsCsvAuto, apiExportRecordsCsvManual } from "helpers/api/records";
import dayjs from "dayjs";
import { TYPE_DATE_OPTIONS_VALUE } from "pages/graph/constants";

const ChartWeekMonth = ({ data, categories, startDate, endDate, patientId, dateSelected, chartType }) => {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState({ temperature: [], faceUp: [], faceDown: [] });
  const [exportType, setExportType] = useState(0);
  const printRef = useRef();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const newFaceUp = [];
    const newFaceDown = [];
    const newTemperature = [];
    for (const cat of categories) {
      if (data.hasOwnProperty('faceUp')) {
        if (data['faceUp'][cat] === undefined) {
          newFaceUp.push(null);
        } else {
          newFaceUp.push(data['faceUp'][cat]);
        }
      }

      if (data.hasOwnProperty('faceDown')) {
        if (data['faceDown'][cat] === undefined) {
          newFaceDown.push(null);
        } else {
          newFaceDown.push(data['faceDown'][cat]);
        }
      }

      if (data.hasOwnProperty('temperature')) {
        if (data['temperature'][cat] === undefined) {
          newTemperature.push(null)
        } else {
          newTemperature.push(data['temperature'][cat]);
        }
      }
    }

    setChartData({
      temperature: newTemperature,
      faceUp: newFaceUp,
      faceDown: newFaceDown
    });
  }, [data, categories]);

  const options = {
    title: {
      text: "",
    },
    chart: {
      type: "line",
      height: 700,
      marginTop: 30,
    },
    xAxis: {
      categories,
      labels: {
        rotation: -45,
        step: 1,
      },
      gridLineWidth: 1,
      gridLineDashStyle: "dot",
      gridLineColor: "#939598",
      lineWidth: 2,
    },
    yAxis: [
      {
        title: {
          text: t("graph.tempePosi.temperature"),
          align: "high",
          rotation: 0,
          y: 0,
          style: {
            fontSize: 16,
            color: "#000000",
            fontWeight: "bold",
          },
        },
        labels: {
          formatter: function () {
            return this.value > 40 || this.value < 20 ? "" : `${this.value}℃`;
          },
        },
        tickInterval: 10,
        min: 0,
        max: 100,
        lineWidth: 2,
        showFirstLabel: false,
        gridLineWidth: 0,
        plotLines: [
          {
            color: "#FFBD59", // Color of the grid line
            width: 1, // Width of the grid line
            value: 20, // Value on the y-axis where the grid line will be shown
            zIndex: 1, // Z-index to control the order of the grid line relative to other elements
          },
          {
            color: "#FFBD59", // Color of the grid line
            width: 1, // Width of the grid line
            value: 30, // Value on the y-axis where the grid line will be shown
            zIndex: 1, // Z-index to control the order of the grid line relative to other elements
          },
          {
            color: "#FFBD59", // Color of the grid line
            width: 1, // Width of the grid line
            value: 40, // Value on the y-axis where the grid line will be shown
            zIndex: 1, // Z-index to control the order of the grid line relative to other elements
          },
        ],
      },
      {
        // id: "yAxis-urine",
        title: {
          text: t("graph.tempePosi.numberOfTime"),
          align: "high",
          rotation: 0,
          y: -15,
          x: -50,
          style: {
            fontSize: 16,
            color: "#000000",
            fontWeight: "bold",
          },
        },
        // tickLength: 0,
        opposite: true,
        lineWidth: 2,
        min: 0,
        max: 100,
        // tickPositions: [0, 5, 10, 15, 20, 25, 30],
        gridLineWidth: 0,
        showFirstLabel: false,
      },
    ],
    series: [
      {
        name: t("graph.tempePosi.temperature"),
        data: chartData?.temperature,
        color: "#004AAD",
        showInLegend: false,
        zIndex: 2,
      },
      {
        name: t("graph.tempePosi.faceUp"),
        data: chartData?.faceUp,
        type: "column",
        color: "#7ED957",
        stack: "numberOfTime",
      },
      {
        name: t("graph.tempePosi.faceDown"),
        data: chartData?.faceDown,
        type: "column",
        color: "#CF69CF",
        stack: "numberOfTime",
      },
    ],
    legend: {
      layout: "vertical",
      align: "left",
      verticalAlign: "bottom",
    },
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      column: {
        stacking: "normal",
        dataLabels: {
          //   enabled: true,
        },
      },
      line: {
        marker: {
          enabled: false,
        },
      },
    },
  };

  const EXPORT_OPTIONS_VALUE = {
    PDF: 0,
    CSV_MANUAL: 1,
    CSV_AUTO: 2,
  };

  const exportOption =
    [
      {
        label: t("graph.temperaturePosition"),
        value: EXPORT_OPTIONS_VALUE.PDF,
        prefix: "/icons/pdf.webp",
      }
    ];

  const handleExport = async () => {
    setIsLoading(true);
    switch (exportType) {
      case EXPORT_OPTIONS_VALUE.PDF:
        exportPDF();
        break;
      case EXPORT_OPTIONS_VALUE.CSV_MANUAL:
        exportCSV(exportType);
        break;
      case EXPORT_OPTIONS_VALUE.CSV_AUTO:
        exportCSV(exportType);
        break;
      default:
        setIsLoading(false);
        break;
    }
  };

  const exportPDF = () => {
    if (printRef.current) {
      const element = printRef.current;
      const images1 = element.querySelectorAll('svg image');
      images1.forEach((img) => {
        img.outerHTML = img.outerHTML.replace("xlink:", "")
      });
      const elementToExport = element.cloneNode(true);
      const images = elementToExport.querySelectorAll('svg image');
      images.forEach((img) => {
        img.outerHTML = img.outerHTML.replace("xlink:", "")
      });
      elementToExport.style.padding = "30px 20px";
      elementToExport.style.backgroundImage = 'url(/background_urine.png)';

      const temp = document.createElement('div');
      temp.style.position = 'absolute';
      temp.style.left = '-9999px';

      // Title in the PDF file.
      const title = document.createElement('h1');
      title.textContent = t("graph.exportPDF.title");
      title.style.fontWeight = "600";
      title.style.fontSize = "1.8em";
      title.style.marginBottom = "0";

      // Time period.
      const timeString = document.createElement('span');
      timeString.textContent = `${t("graph.exportPDF.timeStr")}${moment(startDate).format("YYYY年M月D日分")}～${moment(endDate).format("YYYY年M月D日分")}`;
      timeString.style.fontSize = "1.2em";

      // Copyright.
      const copyright = document.createElement('span');
      copyright.textContent = t("graph.exportPDF.copyright");
      copyright.style.display = "block";
      copyright.style.textAlign = "right";
      copyright.style.fontSize = "0.6em";

      const PDFHeader = document.createElement('div');
      PDFHeader.style.textAlign = "center";
      PDFHeader.style.padding = '10px';
      PDFHeader.style.margin = '5px 0';
      PDFHeader.appendChild(title);
      PDFHeader.appendChild(timeString);

      elementToExport.style.padding = "10px";
      elementToExport.insertBefore(PDFHeader, elementToExport.firstChild);
      elementToExport.appendChild(copyright);

      // Custom style for chart
      const charts = elementToExport.getElementsByClassName('highcharts-background');
      const plotCharts = elementToExport.getElementsByClassName('highcharts-plot-background');
      for (const el of charts) {
        el.style.fill = "none";
      }

      for (const el of plotCharts) {
        el.style.fill = "#ffffff";
      }

      temp.appendChild(elementToExport);
      document.body.appendChild(temp);

      html2canvas(elementToExport).then((canvas) => {
        console.log(canvas);
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'pt',
          format: [canvas.width, canvas.height]
        });

        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

        let nameFile = 'download';
        if (chartType === TYPE_DATE_OPTIONS_VALUE.week) {
          nameFile = t("graph.exportPDF.fileNameWeekChart");
        } else if (chartType === TYPE_DATE_OPTIONS_VALUE.month) {
          nameFile = t("graph.exportPDF.fileNameMonthChart");
        }
        pdf.save(`${nameFile}.pdf`);


        document.body.removeChild(temp);
        setIsLoading(false);
      }).catch((err) => {
        console.error("Error exporting : ", err);
      });
    } else {
      setIsLoading(false);
    }
  };

  const exportCSV = async (type) => {
    const from = dayjs(dateSelected)
      .startOf("month")
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD hh:mm");
    const to = dayjs(dateSelected)
      .endOf("month")
      .endOf("day")
      .utc()
      .format("YYYY-MM-DD hh:mm");

    let res = {};

    switch (type) {
      case EXPORT_OPTIONS_VALUE.CSV_MANUAL:
        res = await apiExportRecordsCsvManual({ from, to, patient_id: patientId });
        break;
      case EXPORT_OPTIONS_VALUE.CSV_AUTO:
        res = await apiExportRecordsCsvAuto({ from, to, patient_id: patientId });
        break;
      default:
        break;
    }

    if (res.data) {
      window.location.href = res.data;
    }
    setIsLoading(false);
    return false;
  }

  return (
    <div>
      <div ref={printRef} id="highchartDataId">
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
      <div className="export-section" style={{ marginTop: "0" }}>
        <SelectComponent
          options={exportOption}
          popupClassName="export-section-options"
          onChange={(e) => setExportType(e)}
          defaultValue={EXPORT_OPTIONS_VALUE.PDF}
          optionRender={(option) => {
            return (
              <div
                style={{
                  alignItems: "center",
                  fontWeight: "bold",
                }}
              >
                <img
                  src={option?.data?.prefix}
                  width="30px"
                  style={{ marginRight: "10px" }}
                />
                {option.label}
              </div>
            );
          }}
        />
        <ButtonComponent
          background="#035697"
          className="export-button"
          text={t("graph.calendarPage.export")}
          type="button"
          onClick={handleExport}
        />
      </div>
      {isLoading && <Loading />}
    </div>
  );
};

export default ChartWeekMonth;
